import React from 'react';
import { BookOpen, Download, Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import { Progress } from 'components/ui/progress';

interface AggregateCourse {
  id: number;
  name: string;
  description?: string;
  aggregateProgressPercent: number;
  assignedUserCount: number;
  notStartedCount: number;
  inProgressCount: number;
  completedCount: number;
  lockedCount?: number;
}

interface OrgCourseProgressProps {
  courses: AggregateCourse[];
  onExport: () => void;
  isExporting: boolean;
}

export const OrgCourseProgress: React.FC<OrgCourseProgressProps> = ({ courses, onExport, isExporting }) => {
  return (
    <section aria-labelledby="org-course-progress-heading" className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 id="org-course-progress-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
            Aggregate Course Progress
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Aggregate progress is calculated from active users who currently have each course assigned.
          </p>
        </div>
        <Button
          type="button"
          size="auto"
          data-testid="org-admin-export-aggregate-progress"
          onClick={onExport}
          disabled={isExporting || courses.length === 0}
        >
          {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
          {isExporting ? 'Exporting...' : 'Export Aggregate Progress'}
        </Button>
      </div>

      {courses.length === 0 ? (
        <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
          <CardContent variant="custom" className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BookOpen className="size-5" />
            </div>
            <h3 className="text-base font-semibold text-foreground">No org default courses</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              There are no default courses configured for this organization.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {courses.map((course) => (
            <Card key={course.id} disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
              <CardContent variant="custom" className="space-y-4 p-6">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">{course.name}</h3>
                  <p className="text-sm text-muted-foreground">{course.description || 'Organization default course'}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Aggregate progress percent</span>
                    <span className="font-medium text-foreground">{Math.round(course.aggregateProgressPercent)}%</span>
                  </div>
                  <Progress value={course.aggregateProgressPercent} className="h-2" />
                </div>

                <div className="flex flex-wrap gap-2">
                  {typeof course.lockedCount === 'number' && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-800">Locked: {course.lockedCount}</Badge>}
                  <Badge variant="outline" className="text-xs">
                    <span className="text-muted-foreground">Assigned:</span>
                    <span className="ml-1 font-medium">{course.assignedUserCount}</span>
                  </Badge>
                  <Badge variant="outline" className="bg-muted/50 text-xs">
                    <span className="text-muted-foreground">Not started:</span>
                    <span className="ml-1 font-medium">{course.notStartedCount}</span>
                  </Badge>
                  <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-xs">
                    <span>In progress:</span>
                    <span className="ml-1 font-medium">{course.inProgressCount}</span>
                  </Badge>
                  <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 text-xs">
                    <span>Completed:</span>
                    <span className="ml-1 font-medium">{course.completedCount}</span>
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
};
